import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { IntlShape, useIntl } from 'react-intl';

import * as z from 'zod';

import { T } from '@/components/i18n/message.i18n.component';
import Button from '@/components/ui/button/button.component';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form/form.component';

import { MappedFormItem, determineInputField, genericSchema } from './form-map.component.core';
import { formMapMessages } from './form-map.messages';

type MappedFormProps = {
  context: string;
  loading: boolean;
  defaultValues: z.infer<ReturnType<typeof genericSchema>>;
  initialValues?: z.infer<ReturnType<typeof genericSchema>>;
  formMap: MappedFormItem[];
  validationSchema: (intl: IntlShape, context: string) => ReturnType<typeof genericSchema>;
  onSubmit: (values: z.infer<ReturnType<typeof genericSchema>>) => void;
  onClose: () => void;
};

export const MappedForm = ({
  context,
  loading,
  defaultValues,
  initialValues,
  formMap,
  validationSchema,
  onSubmit,
  onClose: handleClose,
}: MappedFormProps) => {
  const intl = useIntl();

  const form = useForm<z.infer<ReturnType<typeof genericSchema>>>({
    resolver: zodResolver(validationSchema(intl, context)),
    defaultValues: initialValues || defaultValues,
  });

  return (
    <Form {...form} data-testid={`${context}-form-container`}>
      <form
        data-testid={`${context}-form-form`}
        onSubmit={form.handleSubmit(onSubmit)}
        className="space-y-4 active:border-none"
      >
        {formMap.map((mappedField) => (
          <FormField
            key={mappedField.name}
            control={form.control}
            name={mappedField.name}
            render={({ field }) => (
              <FormItem>
                <div className="flex flex-row justify-between items-center mb-3 ">
                  <FormLabel data-testid={`${context}-form-label-${mappedField.name}`} required={mappedField.required}>
                    {mappedField.label}
                  </FormLabel>
                  <FormMessage data-testid={`${context}-form-error-${mappedField.name}`} />
                </div>
                <FormControl>{determineInputField(mappedField, field, context)}</FormControl>
              </FormItem>
            )}
          />
        ))}
        <div className="flex flex-row justify-between">
          <Button
            data-testid={`${context}-form-button-cancel`}
            disabled={loading}
            onClick={handleClose}
            type="button"
            variant="secondary"
          >
            <T {...formMapMessages.cancelButton} />
          </Button>
          <Button
            data-testid={`${context}-form-button-submit`}
            loading={loading}
            disabled={!form.formState.isDirty}
            type="submit"
            variant="default"
          >
            <T {...formMapMessages.submitButton} />
          </Button>
        </div>
      </form>
    </Form>
  );
};
